import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleLang = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const currentLang = i18n.language === "ja" ? "ja" : "en";

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "bg-white shadow-sm py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://static.readdy.ai/image/96a0c724104ef419e66a29be1c3abef3/cfb1010334eba7537ffb79d0b055fa17.png"
            alt="Cornerstone Strategy Logo"
            className="h-10 w-auto object-contain"
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          <button
            onClick={() => scrollTo("background")}
            className={`text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
              scrolled ? "text-[#1a1a1a] hover:text-[#BF9B5E]" : "text-white/90 hover:text-white"
            }`}
          >
            {t("nav_about")}
          </button>
          <button
            onClick={() => scrollTo("activities")}
            className={`text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
              scrolled ? "text-[#1a1a1a] hover:text-[#BF9B5E]" : "text-white/90 hover:text-white"
            }`}
          >
            {t("nav_activities")}
          </button>
          <button
            onClick={() => scrollTo("vision")}
            className={`text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
              scrolled ? "text-[#1a1a1a] hover:text-[#BF9B5E]" : "text-white/90 hover:text-white"
            }`}
          >
            {t("nav_vision")}
          </button>
          <button
            onClick={() => scrollTo("cta")}
            className={`px-5 py-2 text-sm font-medium border tracking-wide transition-all cursor-pointer whitespace-nowrap ${
              scrolled
                ? "border-[#BF9B5E] text-[#BF9B5E] hover:bg-[#BF9B5E] hover:text-white"
                : "border-white text-white hover:bg-white hover:text-[#1a1a1a]"
            }`}
          >
            {t("nav_join")}
          </button>

          {/* Language Switcher */}
          <div className="flex items-center gap-0 border border-current rounded-sm overflow-hidden">
            <button
              onClick={() => toggleLang("en")}
              className={`px-3 py-1.5 text-xs font-semibold tracking-widest transition-all cursor-pointer whitespace-nowrap ${
                currentLang === "en"
                  ? scrolled
                    ? "bg-[#1a1a1a] text-white"
                    : "bg-white text-[#1a1a1a]"
                  : scrolled
                  ? "text-[#1a1a1a] hover:bg-gray-100"
                  : "text-white hover:bg-white/20"
              }`}
            >
              EN
            </button>
            <button
              onClick={() => toggleLang("ja")}
              className={`px-3 py-1.5 text-xs font-semibold tracking-widest transition-all cursor-pointer whitespace-nowrap ${
                currentLang === "ja"
                  ? scrolled
                    ? "bg-[#1a1a1a] text-white"
                    : "bg-white text-[#1a1a1a]"
                  : scrolled
                  ? "text-[#1a1a1a] hover:bg-gray-100"
                  : "text-white hover:bg-white/20"
              }`}
            >
              JP
            </button>
          </div>
        </nav>

        {/* Mobile: lang switcher + hamburger */}
        <div className="flex md:hidden items-center gap-3">
          <div className="flex items-center border border-current rounded-sm overflow-hidden">
            <button
              onClick={() => toggleLang("en")}
              className={`px-2.5 py-1 text-xs font-semibold cursor-pointer whitespace-nowrap ${
                currentLang === "en"
                  ? scrolled ? "bg-[#1a1a1a] text-white" : "bg-white text-[#1a1a1a]"
                  : scrolled ? "text-[#1a1a1a]" : "text-white"
              }`}
            >
              EN
            </button>
            <button
              onClick={() => toggleLang("ja")}
              className={`px-2.5 py-1 text-xs font-semibold cursor-pointer whitespace-nowrap ${
                currentLang === "ja"
                  ? scrolled ? "bg-[#1a1a1a] text-white" : "bg-white text-[#1a1a1a]"
                  : scrolled ? "text-[#1a1a1a]" : "text-white"
              }`}
            >
              JP
            </button>
          </div>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`w-6 h-6 flex items-center justify-center cursor-pointer ${scrolled ? "text-[#1a1a1a]" : "text-white"}`}
          >
            <i className={`ri-${menuOpen ? "close" : "menu"}-line text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          <button onClick={() => scrollTo("background")} className="text-sm text-[#1a1a1a] hover:text-[#BF9B5E] text-left cursor-pointer whitespace-nowrap">
            {t("nav_about")}
          </button>
          <button onClick={() => scrollTo("activities")} className="text-sm text-[#1a1a1a] hover:text-[#BF9B5E] text-left cursor-pointer whitespace-nowrap">
            {t("nav_activities")}
          </button>
          <button onClick={() => scrollTo("vision")} className="text-sm text-[#1a1a1a] hover:text-[#BF9B5E] text-left cursor-pointer whitespace-nowrap">
            {t("nav_vision")}
          </button>
          <button
            onClick={() => scrollTo("cta")}
            className="px-4 py-2 bg-[#BF9B5E] text-white text-sm font-medium text-left cursor-pointer whitespace-nowrap w-fit"
          >
            {t("nav_join")}
          </button>
        </div>
      )}
    </header>
  );
}
